<script lang="ts">
  import { goto, yyyy, zzzz } from "../store.svelte";
  import { toast } from "../lib/toast.svelte.ts";
  import Fa from "svelte-fa";
  import {
    faCheckCircle,
    faExclamationTriangle,
    faKeyboard,
    faList,
    faCheck,
    faTimes,
  } from "@fortawesome/free-solid-svg-icons";

  const url = yyyy.uri.api;

  type PhaseKey = "ABC" | "AB" | "AC" | "BC" | "A" | "B" | "C";
  const phases: PhaseKey[] = ["ABC", "AB", "AC", "BC", "A", "B", "C"];

  const geomopts = [
    "PointZ", "MultiPointZ", "LineStringZ", "MultiLineStringZ",
    "Point", "MultiPoint", "LineString", "MultiLineString",
  ];
  const pointopts = ["PointZ", "MultiPointZ", "Point", "MultiPoint"];

  // Track state for each layer
  type LayerState = {
    ischecked: boolean;
    inprogress: boolean;
    sentoff: boolean | string;
    checkphase: boolean;
    checkuniq: boolean;
    notunique: boolean | undefined;
    optphases: string[];
    isElectrical: boolean;
    isTransformer: boolean;
    participant: {
      schemaname: string;
      tablename: string;
      primarykey: string;
      geomcolumn: string;
      electrical: {
        feederid: string;
        sourcequery: string;
        blockquery: string;
        phasecolumn: string;
        phasemap: Record<PhaseKey, string>;
      };
    };
    voltagetier: {
      primary: string;
      secondary: string;
    };
    transformer: {
      avoltampere: string;
      bvoltampere: string;
      cvoltampere: string;
    };
    select_or_input: Record<PhaseKey, boolean>;
  };

  // Initialize all layer states upfront
  const createInitialStates = (): Record<string, LayerState> => {
    const states: Record<string, LayerState> = {};
    if (yyyy.createNewLiveWire.layers) {
      for (const layer of yyyy.createNewLiveWire.layers) {
        states[layer.relname] = {
          ischecked: false,
          inprogress: false,
          sentoff: false,
          checkphase: false,
          checkuniq: false,
          notunique: undefined,
          optphases: [],
          isElectrical: true,
          isTransformer: false,
          participant: {
            schemaname: yyyy.createNewLiveWire.data.wireident,
            tablename: layer.relname,
            primarykey: "",
            geomcolumn: "",
            electrical: {
              feederid: "",
              sourcequery: "",
              blockquery: "",
              phasecolumn: "",
              phasemap: { ABC: "", AB: "", AC: "", BC: "", A: "", B: "", C: "" },
            },
          },
          voltagetier: {
            primary: "",
            secondary: "",
          },
          transformer: {
            avoltampere: "",
            bvoltampere: "",
            cvoltampere: "",
          },
          select_or_input: { ABC: true, AB: true, AC: true, BC: true, A: true, B: true, C: true },
        };
      }
    }
    return states;
  };

  let layerStates = $state<Record<string, LayerState>>(createInitialStates());
  let selectedLayer = $state<string | null>(null);
  let submitting = $state(false);
  let submitCount = $state(0);
  let finishing = $state(false);
  let cancelling = $state(false);
  let copying = $state(false);

  // Helper to get geometry columns for a layer
  const getGeoCols = (layerInfo: Array<{ column: string; type: string; modifier: string }>) => {
    return layerInfo.filter((col) => {
      if (col.type !== "geometry") return false;
      const modifier = col.modifier?.split(",")[0]?.slice(1);
      return modifier && geomopts.includes(modifier);
    });
  };

  // Helper to check if layer is point type
  const isPointLayer = (layerInfo: Array<{ column: string; type: string; modifier: string }>) => {
    const geo = getGeoCols(layerInfo);
    if (geo.length === 0) return false;
    const modifier = geo[0].modifier?.split(",")[0]?.slice(1);
    return modifier && pointopts.includes(modifier);
  };

  // Get layer info by name
  const getLayerInfo = (layerName: string) => {
    const layer = yyyy.createNewLiveWire.layers.find((l: any) => l.relname === layerName);
    return layer?.json_agg || [];
  };

  // Toggle layer participation
  const toggleLayer = async (layerName: string) => {
    const state = layerStates[layerName];
    if (!state) return;

    const layerInfo = getLayerInfo(layerName);
    const geo = getGeoCols(layerInfo);

    if (!state.ischecked && geo.length === 0) {
      alert("This layer has no valid geometry columns. It cannot participate in the LiveWire");
      return;
    }

    state.inprogress = true;
    const operation = state.ischecked ? "DROP" : "ADD";

    const _body = {
      wireident: yyyy.createNewLiveWire.data.wireident,
      layer_name: layerName,
      operation,
      ext_schema: yyyy.createNewLiveWire.data.ext_schema,
    };
    const _hdrs = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + yyyy.sesh.logtoken.token,
    };

    try {
      const response = await fetch(`${url}/rpc/create_livewire_layer_ops`, {
        method: "post",
        body: JSON.stringify(_body),
        headers: _hdrs,
      });

      if (response.ok) {
        state.ischecked = !state.ischecked;
        if (!state.ischecked && selectedLayer === layerName) {
          selectedLayer = null;
        }
      } else {
        alert(`Something went wrong ${operation === "ADD" ? "adding" : "dropping"} layer`);
      }
    } catch (err) {
      console.error(err);
      alert("Error toggling layer");
    }

    state.inprogress = false;
  };

  // Select layer for configuration
  const selectLayer = (layerName: string) => {
    const state = layerStates[layerName];
    if (state?.ischecked) {
      selectedLayer = layerName;
    }
  };

  // Check unique column
  const uniqcheck = async (layerName: string) => {
    const state = layerStates[layerName];
    if (!state || !yyyy.sesh.logtoken) return;

    state.checkuniq = true;
    const _hdrs = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + yyyy.sesh.logtoken.token,
    };
    const _body = {
      layer_name: layerName,
      wireident: yyyy.createNewLiveWire.data.wireident,
      column_name: state.participant.primarykey,
    };

    try {
      const rspns = await fetch(`${url}/rpc/create_livewire_check_unique_column`, {
        method: "post",
        body: JSON.stringify(_body),
        headers: _hdrs,
      });

      if (rspns.ok) {
        state.notunique = undefined;
      } else {
        alert("This column is not unique and/or has null values");
        state.notunique = true;
      }
    } catch (err) {
      console.error(err);
    }

    state.checkuniq = false;
  };

  // Get phase values for column
  const phasecheck = async (layerName: string) => {
    const state = layerStates[layerName];
    if (!state || !yyyy.sesh.logtoken) return;

    state.checkphase = true;
    const _hdrs = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + yyyy.sesh.logtoken.token,
    };
    const _body = {
      layer_name: layerName,
      wireident: yyyy.createNewLiveWire.data.wireident,
      column_name: state.participant.electrical.phasecolumn,
    };

    try {
      const rspns = await fetch(`${url}/rpc/create_livewire_get_phases_for_column`, {
        method: "post",
        body: JSON.stringify(_body),
        headers: _hdrs,
      });

      if (rspns.ok) {
        const data = await rspns.json();
        state.optphases = data.phaseinfo || [];
      } else {
        alert("Something went wrong getting phases");
      }
    } catch (err) {
      console.error(err);
    }

    state.checkphase = false;
  };

  // Toggle select/input for phase mapping
  const toggleSelectInput = (layerName: string, phase: PhaseKey) => {
    const state = layerStates[layerName];
    if (state) {
      state.select_or_input[phase] = !state.select_or_input[phase];
    }
  };

  // Submit all participants in parallel
  const addParticipants = async () => {
    submitting = true;

    const enabledLayers = Object.entries(layerStates).filter(([_, state]) => state.ischecked && !state.sentoff);

    const _hdrs = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + yyyy.sesh.logtoken.token,
    };

    // Create array of promises for parallel execution
    const promises = enabledLayers.map(async ([layerName, state]) => {
      const layerInfo = getLayerInfo(layerName);
      const isPoint = isPointLayer(layerInfo);

      state.sentoff = "submitting";

      // Build participant object - use electrical: false for non-electrical layers
      let participant;
      if (!state.isElectrical) {
        participant = {
          schemaname: state.participant.schemaname,
          tablename: state.participant.tablename,
          primarykey: state.participant.primarykey,
          geomcolumn: state.participant.geomcolumn,
          electrical: false,
        };
      } else {
        // Build electrical object
        const electrical: any = {
          ...state.participant.electrical,
        };

        if (state.isTransformer) {
          electrical.transformer = { ...state.transformer };
        } else {
          electrical.voltagetier = { ...state.voltagetier };
        }

        participant = {
          ...state.participant,
          electrical,
        };
      }

      const _body = {
        layerispoint: isPoint,
        wireident: yyyy.createNewLiveWire.data.wireident,
        participant,
      };

      try {
        const res = await fetch(`${url}/rpc/create_livewire_add_config_participant`, {
          method: "post",
          body: JSON.stringify(_body),
          headers: _hdrs,
        });

        if (res.ok) {
          console.log(`${layerName} added successfully`);
          state.sentoff = true;
        } else {
          const data = await res.json();
          console.error(`Failed to add ${layerName}:`, data);
          alert(`Failed to add ${layerName}`);
          state.sentoff = false;
        }
      } catch (err) {
        console.error(err);
        state.sentoff = false;
      }
    });

    // Wait for all to complete
    await Promise.all(promises);

    submitting = false;
  };

  // Finish configuration
  const finishConfig = async () => {
    finishing = true;

    const _body = JSON.stringify({ wireident: yyyy.createNewLiveWire.data.wireident });
    const _hdrs = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + yyyy.sesh.logtoken.token,
    };

    try {
      const response = await fetch(`${url}/rpc/create_livewire_finish_config`, {
        method: "post",
        body: _body,
        headers: _hdrs,
      });

      if (!response.ok) {
        const data = await response.json();
        console.error("finish_config failed:", data);
        alert("Something went wrong finishing configuration");
        finishing = false;
        return;
      }

      // Create search index
      await fetch(`${url}/rpc/create_livewire_search_index`, {
        method: "post",
        body: _body,
        headers: _hdrs,
      });

      delete yyyy.createNewLiveWire;
      zzzz.emit("refreshlivewires");
      goto("/livewires");
    } catch (err) {
      console.error(err);
      alert("Error finishing configuration");
      finishing = false;
    }
  };

  // Cancel and delete
  const cancelAdd = async () => {
    cancelling = true;
    const _hdrs = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + yyyy.sesh.logtoken.token,
    };

    try {
      await fetch(`${url}/livewires?wireident=eq.${yyyy.createNewLiveWire.data.wireident}`, {
        method: "DELETE",
        headers: _hdrs,
      });
    } catch (err) {
      console.error(err);
    }

    delete yyyy.createNewLiveWire;
    goto("/livewires");
  };

  // Check if all enabled layers have been submitted
  const allSubmitted = $derived.by(() => {
    const enabled = Object.values(layerStates).filter((s) => s.ischecked);
    return enabled.length > 0 && enabled.every((s) => s.sentoff === true);
  });

  // Count enabled layers
  const enabledCount = $derived(Object.values(layerStates).filter((s) => s.ischecked).length);

  // Check if a layer config is complete
  const isLayerConfigComplete = (state: LayerState, layerName?: string): boolean => {
    // Required for all layers
    if (!state.participant.primarykey || !state.participant.geomcolumn) return false;

    // If electrical, check electrical fields
    if (state.isElectrical) {
      const elec = state.participant.electrical;
      if (!elec.feederid || !elec.phasecolumn) return false;
      // Check at least one phase mapping is set
      const hasPhaseMapping = Object.values(elec.phasemap).some(v => v && v.trim() !== "");
      if (!hasPhaseMapping) return false;

      // For transformer, check transformer fields
      if (state.isTransformer) {
        if (!state.transformer.avoltampere || !state.transformer.avoltampere.trim()) return false;
        if (!state.transformer.bvoltampere || !state.transformer.bvoltampere.trim()) return false;
        if (!state.transformer.cvoltampere || !state.transformer.cvoltampere.trim()) return false;
      } else {
        // For non-transformer, check voltage tier fields
        if (!state.voltagetier.primary || !state.voltagetier.primary.trim()) return false;
        if (!state.voltagetier.secondary || !state.voltagetier.secondary.trim()) return false;
      }

      // For point layers, source and block queries are required
      if (layerName) {
        const layerInfo = getLayerInfo(layerName);
        if (isPointLayer(layerInfo)) {
          if (!elec.sourcequery || !elec.sourcequery.trim()) return false;
          if (!elec.blockquery || !elec.blockquery.trim()) return false;
        }
      }
    }

    return true;
  };

  // Check if all enabled layers have complete configs
  const allLayersConfigComplete = $derived.by(() => {
    const enabledLayers = Object.entries(layerStates).filter(([_, s]) => s.ischecked && s.sentoff !== true);
    if (enabledLayers.length === 0) return false;
    return enabledLayers.every(([name, state]) => isLayerConfigComplete(state, name));
  });

  // Copy current config to all other active layers
  const copyConfigToAllActive = async () => {
    if (!selectedLayer || copying) return;

    const sourceState = layerStates[selectedLayer];
    if (!sourceState) return;

    copying = true;

    // Small delay for visual feedback
    await new Promise(resolve => setTimeout(resolve, 1000));

    let copiedCount = 0;
    for (const [layerName, state] of Object.entries(layerStates)) {
      // Skip the source layer and unchecked layers
      if (layerName === selectedLayer || !state.ischecked) continue;
      // Skip already submitted layers
      if (state.sentoff === true) continue;

      // Get target layer's column options
      const targetLayerInfo = getLayerInfo(layerName);
      const targetGeoCols = getGeoCols(targetLayerInfo);
      const targetGeoColNames = targetGeoCols.map(c => c.column);
      const targetColNames = targetLayerInfo.map((c: any) => c.column);

      // Copy column mappings - only if column exists in target
      if (targetColNames.includes(sourceState.participant.primarykey)) {
        state.participant.primarykey = sourceState.participant.primarykey;
      }
      if (targetGeoColNames.includes(sourceState.participant.geomcolumn)) {
        state.participant.geomcolumn = sourceState.participant.geomcolumn;
      }

      // Copy electrical settings
      state.isElectrical = sourceState.isElectrical;
      state.isTransformer = sourceState.isTransformer;

      // Feeder ID and phase column - only if column exists in target
      if (targetColNames.includes(sourceState.participant.electrical.feederid)) {
        state.participant.electrical.feederid = sourceState.participant.electrical.feederid;
      }
      if (targetColNames.includes(sourceState.participant.electrical.phasecolumn)) {
        state.participant.electrical.phasecolumn = sourceState.participant.electrical.phasecolumn;
      }

      // Source/block queries - copy directly
      state.participant.electrical.sourcequery = sourceState.participant.electrical.sourcequery;
      state.participant.electrical.blockquery = sourceState.participant.electrical.blockquery;

      // Phase mappings - force copy regardless of dropdown options
      state.participant.electrical.phasemap = { ...sourceState.participant.electrical.phasemap };

      // Voltage tier - copy directly
      state.voltagetier = { ...sourceState.voltagetier };

      // Transformer mappings - copy directly
      state.transformer = { ...sourceState.transformer };

      // Set all phases to textbox mode so user can see copied values
      state.select_or_input = { ABC: false, AB: false, AC: false, BC: false, A: false, B: false, C: false };
      copiedCount++;
    }

    copying = false;
    toast.success(`Config copied to ${copiedCount} layer${copiedCount !== 1 ? 's' : ''}`, 3000, 'top-center');
  };

  // Count other active layers that can receive config
  const copyTargetCount = $derived.by(() => {
    if (!selectedLayer) return 0;
    return Object.entries(layerStates).filter(
      ([name, state]) => name !== selectedLayer && state.ischecked && state.sentoff !== true
    ).length;
  });

  // Check if current layer config is complete
  const currentConfigComplete = $derived.by(() => {
    if (!selectedLayer) return false;
    const state = layerStates[selectedLayer];
    if (!state) return false;
    return isLayerConfigComplete(state, selectedLayer);
  });
</script>

<div class="configure-container">
  <div class="configure-card">
    <header class="configure-header">
      <h1 class="title">Configure Layers</h1>
      <p class="subtitle">Select layers to include and configure their mappings</p>
    </header>

    <div class="configure-body">
      <!-- Left Panel: Layer List -->
      <div class="layer-list">
        <div class="layer-list-header">
          <span>Layers ({enabledCount} enabled)</span>
        </div>
        <div class="layer-items">
          {#each [...yyyy.createNewLiveWire.layers].sort((a, b) => a.relname.localeCompare(b.relname)) as layer}
            {@const state = layerStates[layer.relname]}
            {@const geo = getGeoCols(layer.json_agg)}
            {#if state}
              <div
                class="layer-item"
                class:selected={selectedLayer === layer.relname}
                class:disabled={geo.length === 0}
                class:clickable={state.ischecked}
                onclick={() => state.ischecked && selectLayer(layer.relname)}
              >
                <div class="layer-toggle" onclick={(e) => e.stopPropagation()}>
                  {#if state.inprogress}
                    <span class="toggle-spinner"></span>
                  {:else}
                    <button
                      type="button"
                      class="toggle-btn"
                      class:active={state.ischecked}
                      disabled={geo.length === 0}
                      onclick={() => toggleLayer(layer.relname)}
                    >
                      <span class="toggle-knob"></span>
                    </button>
                  {/if}
                </div>
                <div class="layer-name" class:checked={state.ischecked}>
                  {layer.relname}
                  {#if geo.length === 0}
                    <span class="no-geom">(no geometry)</span>
                  {/if}
                </div>
                {#if state.ischecked}
                  <div class="layer-status">
                    {#if state.sentoff === "submitting"}
                      <span class="status-spinner"></span>
                    {:else if state.sentoff === true}
                      <Fa icon={faCheckCircle} class="sent-icon" />
                    {:else if isLayerConfigComplete(state, layer.relname)}
                      <Fa icon={faCheck} class="complete-icon" />
                    {:else}
                      <Fa icon={faExclamationTriangle} class="incomplete-icon" />
                    {/if}
                  </div>
                {/if}
              </div>
            {/if}
          {/each}
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          {#if !allSubmitted}
            <button
              class="button is-success is-fullwidth"
              class:is-loading={submitting}
              disabled={enabledCount === 0 || !allLayersConfigComplete}
              onclick={addParticipants}
            >
              <span class="icon"><Fa icon={faCheck} /></span>
              <span>Add Participants</span>
            </button>
          {:else}
            <button
              class="button is-primary is-fullwidth"
              class:is-loading={finishing}
              onclick={finishConfig}
            >
              <span class="icon"><Fa icon={faCheck} /></span>
              <span>Finish</span>
            </button>
          {/if}
          <button
            class="button is-danger is-outlined is-fullwidth mt-2"
            class:is-loading={cancelling}
            onclick={cancelAdd}
            disabled={submitting || finishing || cancelling}
          >
            <span class="icon"><Fa icon={faTimes} /></span>
            <span>Cancel</span>
          </button>
        </div>
      </div>

      <!-- Right Panel: Configuration Form -->
      <div class="config-form">
        {#if selectedLayer && layerStates[selectedLayer]}
          {@const state = layerStates[selectedLayer]}
          {@const layerInfo = getLayerInfo(selectedLayer)}
          {@const geo = getGeoCols(layerInfo)}
          {@const isPoint = isPointLayer(layerInfo)}

          <div class="form-header">
            <h2>Configuring: {selectedLayer}</h2>
            <div class="header-badges">
              {#if isPoint}
                <span class="layer-type point">Point Layer</span>
              {:else}
                <span class="layer-type line">Line Layer</span>
              {/if}
            </div>
          </div>

          <div class="form-content">
            <!-- Electrical & Transformer Toggles -->
            <section class="form-section electrical-toggle-section">
              <div class="toggle-row">
                <div class="electrical-toggle">
                  <span class="toggle-label">Electrical Layer</span>
                  <button
                    type="button"
                    class="toggle-btn"
                    class:active={state.isElectrical}
                    onclick={() => state.isElectrical = !state.isElectrical}
                  >
                    <span class="toggle-knob"></span>
                  </button>
                </div>

                {#if state.isElectrical}
                  <div class="electrical-toggle">
                    <span class="toggle-label">Transformer</span>
                    <button
                      type="button"
                      class="toggle-btn"
                      class:active={state.isTransformer}
                      onclick={() => state.isTransformer = !state.isTransformer}
                    >
                      <span class="toggle-knob"></span>
                    </button>
                  </div>
                {/if}
              </div>
            </section>

            <!-- Columns Section -->
            <section class="form-section">
              <h3>Column Mapping</h3>
              <div class="form-grid" class:two-col={!state.isElectrical}>
                <div class="field">
                  <label class="label">Geometry Column</label>
                  <div class="control">
                    <div class="select is-fullwidth">
                      <select bind:value={state.participant.geomcolumn}>
                        <option value="" disabled>Select geometry column</option>
                        {#each geo as col}
                          <option value={col.column}>{col.column}</option>
                        {/each}
                      </select>
                    </div>
                  </div>
                </div>

                <div class="field">
                  <label class="label">Primary Key</label>
                  <div class="control" class:is-loading={state.checkuniq}>
                    <div class="select is-fullwidth" class:is-danger={state.notunique}>
                      <select
                        bind:value={state.participant.primarykey}
                        onchange={() => uniqcheck(selectedLayer)}
                      >
                        <option value="" disabled>Select unique column</option>
                        {#each layerInfo as col}
                          <option value={col.column}>{col.column}</option>
                        {/each}
                      </select>
                    </div>
                  </div>
                </div>

                {#if state.isElectrical}
                  <div class="field">
                    <label class="label">Feeder ID Column</label>
                    <div class="control">
                      <div class="select is-fullwidth">
                        <select bind:value={state.participant.electrical.feederid}>
                          <option value="" disabled>Select feeder column</option>
                          {#each layerInfo as col}
                            <option value={col.column}>{col.column}</option>
                          {/each}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div class="field">
                    <label class="label">Phase Column</label>
                    <div class="control" class:is-loading={state.checkphase}>
                      <div class="select is-fullwidth">
                        <select
                          bind:value={state.participant.electrical.phasecolumn}
                          onchange={() => phasecheck(selectedLayer)}
                        >
                          <option value="" disabled>Select phase column</option>
                          {#each layerInfo as col}
                            <option value={col.column}>{col.column}</option>
                          {/each}
                        </select>
                      </div>
                    </div>
                  </div>
                {/if}
              </div>
            </section>

            {#if state.isElectrical}
              <!-- Phase Mapping Section -->
              <section class="form-section">
                <h3>Phase Mapping</h3>
                <div class="phase-grid">
                  {#each phases as phase (phase)}
                    <div class="phase-row">
                      <span class="phase-label">{phase}</span>
                      {#if state.select_or_input[phase]}
                        <div class="select is-small">
                          <select bind:value={state.participant.electrical.phasemap[phase]}>
                            <option value="">--</option>
                            {#each state.optphases as p}
                              <option value={p}>{p}</option>
                            {/each}
                          </select>
                        </div>
                      {:else}
                        <input
                          class="input is-small"
                          type="text"
                          placeholder={phase}
                          bind:value={state.participant.electrical.phasemap[phase]}
                        />
                      {/if}
                      <button
                        type="button"
                        class="button is-small toggle-input-btn"
                        onclick={() => toggleSelectInput(selectedLayer, phase)}
                      >
                        <Fa icon={state.select_or_input[phase] ? faKeyboard : faList} />
                      </button>
                    </div>
                  {/each}
                </div>
              </section>

              <!-- Voltage Tier (non-transformer only) -->
              {#if !state.isTransformer}
              <section class="form-section">
                <h3>Voltage Tier</h3>
                <div class="voltage-tier-grid">
                  <div class="voltage-tier-row">
                    <span class="tier-label">Primary</span>
                    <input
                      class="input is-small"
                      type="text"
                      placeholder="Primary voltage tier column"
                      bind:value={state.voltagetier.primary}
                    />
                  </div>
                  <div class="voltage-tier-row">
                    <span class="tier-label">Secondary</span>
                    <input
                      class="input is-small"
                      type="text"
                      placeholder="Secondary voltage tier column"
                      bind:value={state.voltagetier.secondary}
                    />
                  </div>
                </div>
              </section>
              {/if}

              <!-- Transformer Voltampere Mapping (transformer only) -->
              {#if state.isTransformer}
              <section class="form-section">
                <h3>Transformer Voltampere Mapping</h3>
                <div class="transformer-grid">
                  <div class="transformer-row">
                    <span class="phase-label">A</span>
                    <input
                      class="input is-small"
                      type="text"
                      placeholder="A phase voltampere column"
                      bind:value={state.transformer.avoltampere}
                    />
                  </div>
                  <div class="transformer-row">
                    <span class="phase-label">B</span>
                    <input
                      class="input is-small"
                      type="text"
                      placeholder="B phase voltampere column"
                      bind:value={state.transformer.bvoltampere}
                    />
                  </div>
                  <div class="transformer-row">
                    <span class="phase-label">C</span>
                    <input
                      class="input is-small"
                      type="text"
                      placeholder="C phase voltampere column"
                      bind:value={state.transformer.cvoltampere}
                    />
                  </div>
                </div>
              </section>
              {/if}

              <!-- Source/Block Section (point layers only) -->
              {#if isPoint}
              <section class="form-section">
                <h3>Source & Block Queries</h3>
                <div class="field">
                  <label class="label">Source Query</label>
                  <div class="control">
                    <textarea
                      class="textarea"
                      rows="2"
                      placeholder="Query to select source devices (e.g., TRUE or FALSE)"
                      bind:value={state.participant.electrical.sourcequery}
                    ></textarea>
                  </div>
                </div>
                <div class="field">
                  <label class="label">Block Query</label>
                  <div class="control">
                    <textarea
                      class="textarea"
                      rows="2"
                      placeholder="Query to select block devices (e.g., NOT energized)"
                      bind:value={state.participant.electrical.blockquery}
                    ></textarea>
                  </div>
                </div>
              </section>
              {/if}
            {/if}
          </div>

          <!-- Copy config button - only show when config is complete and there are targets -->
          {#if currentConfigComplete && copyTargetCount > 0}
            <div class="form-footer">
              <button
                class="button is-info is-outlined copy-config-btn"
                class:is-loading={copying}
                disabled={copying}
                onclick={copyConfigToAllActive}
              >
                Copy config to all active layers ({copyTargetCount})
              </button>
            </div>
          {/if}
        {:else}
          <div class="no-selection">
            <p>Select a layer from the list to configure it</p>
            <p class="hint">Toggle layers on, then click on a layer to configure it</p>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  .configure-container {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    width: 70%;
    height: fit-content;
    max-height: 90vh;
    padding: 1rem;
    z-index: 5;
    overflow-y: auto;
  }

  .configure-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .configure-header {
    background: linear-gradient(135deg, #ff3e00 0%, #ff6b35 100%);
    color: white;
    padding: 1.5rem 2rem;
  }

  .configure-header .title {
    color: white;
    font-size: 1.5rem;
    font-weight: 300;
    text-transform: uppercase;
    margin-bottom: 0.25rem;
  }

  .configure-header .subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
  }

  .configure-body {
    display: flex;
    height: 80vh;
  }

  /* Left Panel */
  .layer-list {
    width: 30%;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    background: #fafafa;
  }

  .layer-list-header {
    height: 10%;
    display: flex;
    align-items: center;
    padding: 0 1rem;
    font-weight: 600;
    font-size: 0.85rem;
    color: #666;
    border-bottom: 1px solid #e0e0e0;
    background: #f0f0f0;
    flex-shrink: 0;
  }

  .layer-items {
    height: 80%;
    overflow-y: auto;
  }

  .layer-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #eee;
    gap: 0.75rem;
    transition: background 0.15s;
  }

  .layer-item:hover {
    background: #f5f5f5;
  }

  .layer-item.selected {
    background: #fff3e0;
    border-left: 3px solid #ff3e00;
  }

  .layer-item.disabled {
    opacity: 0.5;
  }

  .layer-toggle {
    flex-shrink: 0;
  }

  .layer-name {
    flex: 1;
    font-size: 0.9rem;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .layer-name.checked {
    color: #333;
    font-weight: 500;
  }

  .layer-item.clickable {
    cursor: pointer;
  }

  .layer-status {
    margin-left: auto;
    flex-shrink: 0;
  }

  .layer-status :global(.sent-icon) {
    color: #48c774;
  }

  .layer-status :global(.complete-icon) {
    color: #48c774;
  }

  .layer-status :global(.incomplete-icon) {
    color: #ffdd57;
  }

  .status-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e0e0e0;
    border-top-color: #ff3e00;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
  }

  .no-geom {
    font-size: 0.75rem;
    color: #999;
    font-style: italic;
  }

  .action-buttons {
    height: 125px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 1rem;
    border-top: 1px solid #e0e0e0;
    background: #f5f5f5;
    flex-shrink: 0;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  /* Right Panel */
  .config-form {
    width: 70%;
    padding: 1.5rem;
    overflow-y: auto;
  }

  .form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #f0f0f0;
  }

  .form-header h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
  }

  .layer-type {
    font-size: 0.75rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-weight: 500;
  }

  .layer-type.point {
    background: #e3f2fd;
    color: #1565c0;
  }

  .layer-type.line {
    background: #f3e5f5;
    color: #7b1fa2;
  }

  .form-section {
    margin-bottom: 1.5rem;
  }

  .form-section h3 {
    font-size: 0.9rem;
    font-weight: 600;
    color: #666;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .form-grid.two-col {
    grid-template-columns: 1fr 1fr;
  }

  .header-badges {
    display: flex;
    gap: 0.5rem;
  }

  .electrical-toggle-section {
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e0e0e0;
  }

  .electrical-toggle {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .toggle-label {
    font-weight: 600;
    font-size: 0.95rem;
  }

  .toggle-row {
    display: flex;
    gap: 2rem;
    align-items: center;
  }

  .voltage-tier-grid {
    display: flex;
    gap: 0.5rem;
  }

  .voltage-tier-row {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
  }

  .voltage-tier-row .input {
    flex: 1;
    width: 100%;
  }

  .tier-label {
    font-weight: 600;
    font-size: 0.85rem;
    color: #555;
    white-space: nowrap;
  }

  .field {
    margin-bottom: 0;
  }

  .label {
    font-size: 0.85rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .phase-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.5rem;
  }

  .phase-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
  }

  .phase-label {
    width: 40px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #555;
  }

  .phase-row .select,
  .phase-row .input {
    flex: 1;
    width: 100%;
  }

  .phase-row .select select {
    width: 100%;
  }

  .transformer-grid {
    display: flex;
    gap: 0.5rem;
  }

  .transformer-row {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
  }

  .transformer-row .input {
    flex: 1;
    width: 100%;
  }

  .toggle-input-btn {
    flex-shrink: 0;
  }

  .form-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e0e0e0;
  }

  .copy-config-btn {
    font-size: 0.85rem;
  }

  .copy-config-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .no-selection {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #999;
    text-align: center;
  }

  .no-selection p {
    margin: 0.5rem 0;
  }

  .no-selection .hint {
    font-size: 0.85rem;
    color: #bbb;
  }

  /* Toggle button styles */
  .toggle-btn {
    width: 44px;
    height: 24px;
    background: #ccc;
    border: none;
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    transition: background 0.2s;
    padding: 0;
  }

  .toggle-btn:hover:not(:disabled) {
    background: #bbb;
  }

  .toggle-btn.active {
    background: #48c774;
  }

  .toggle-btn.active:hover:not(:disabled) {
    background: #3ec46d;
  }

  .toggle-knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  .toggle-btn.active .toggle-knob {
    transform: translateX(20px);
  }

  .toggle-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Toggle spinner */
  .toggle-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #e0e0e0;
    border-top-color: #ff3e00;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
